import { DOCUMENT } from '@angular/common';
import { inject, Service, signal } from '@angular/core';
import { EnvironmentService } from '@indutrust/shared-env';

@Service()
export class GoogleMapsApiLoaderService {
  private readonly document = inject(DOCUMENT);
  private readonly envService = inject(EnvironmentService);

  readonly mapsLoaded = signal(false);

  /**
   * Dynamically loads the Google Maps JavaScript API by appending a `<script>` tag.
   * Resolves immediately if the API has already been loaded.
   * @returns A promise that resolves once the Google Maps API is ready to use.
   */
  async loadGoogleMaps(): Promise<void> {
    if (this.mapsLoaded()) {
      return;
    }

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;

    const callbackName = `gmapsCallback${Date.now()}`;

    const url = new URL('https://maps.googleapis.com/maps/api/js');
    url.searchParams.append('key', this.envService.getGoogleMapsApiKey());
    url.searchParams.append('v', 'weekly');
    url.searchParams.append('callback', callbackName);
    url.searchParams.append('libraries', 'core,maps,marker,geometry');
    url.searchParams.append('loading', 'async');

    script.src = url.toString();

    const promise = new Promise<void>((resolve) => {
      const globalWindow = globalThis.window as Window | undefined;
      if (globalWindow !== undefined) {
        // @ts-expect-error - We are adding a global callback function dynamically named
        globalWindow[callbackName] = () => {
          this.mapsLoaded.set(true);
          resolve();
          // @ts-expect-error - We are deleting a global callback function dynamically named
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete globalWindow[callbackName];
        };
      }
    });

    this.document.body.appendChild(script);
    return promise;
  }
}
